import { Button, FormControl, FormErrorMessage, Input, InputGroup } from '@chakra-ui/react';
import styled from '@emotion/styled';
import { useFormik } from 'formik';
import { useState } from 'react';
import * as zod from 'zod';
import { LangKey } from '../../../intl';
import { formValidate } from '../../../state/forms/helpers.forms';
import { useIntl } from '../../../state/intl';
import TogglePasswordVisibilityButton from './set-password/TogglePasswordVisibilityButton';

interface SetPasswordFormProps {
  loading: boolean;
  handleSubmit: (password: string) => void;
  isReset: boolean;
}

const formSchema = zod.object({
  password: zod
    .string()
    .min(6)
    .max(50),
  repeatPassword: zod
    .string()
    .min(6)
    .max(50)
});

type SetPasswordFormValues = zod.infer<typeof formSchema>;

const Form = styled.form`
  width: 400px;
  height: 100%;
  margin-top: 8px;
`;

const SetPasswordForm: React.FC<SetPasswordFormProps> = ({ loading, handleSubmit, isReset }) => {
  const { lang } = useIntl();

  const [showValues, setShowValues] = useState(false);

  const {
    errors,
    values,
    touched,
    handleChange,
    handleBlur,
    handleSubmit: handleFormSubmit
  } = useFormik<SetPasswordFormValues>({
    validate: formValidate(formSchema, (values, errors) => {
      if (values.password !== values.repeatPassword) {
        errors.repeatPassword = 'The two values are not equal';
      }
    }),
    initialValues: {
      password: '',
      repeatPassword: ''
    },
    onSubmit: values => handleSubmit(values.password)
  });

  const txs: {
    [key in LangKey]: {
      newPsw: string;
      repeatNewPsw: string;
      repeatPsw: string;
    };
  } = {
    en: {
      newPsw: 'New password',
      repeatPsw: 'Repeat password',
      repeatNewPsw: 'Repeat new password'
    },
    it: {
      newPsw: 'Nuova password',
      repeatPsw: 'Ripeti password',
      repeatNewPsw: 'Ripeti nuova password'
    },
    es: {
      newPsw: 'New password',
      repeatPsw: 'Repeat password',
      repeatNewPsw: 'Repeat new password'
    }
  };

  const tx = txs[lang];

  return (
    <Form onSubmit={handleFormSubmit}>
      <FormControl>
        <InputGroup>
          <Input
            variant="outline"
            h="48px"
            id="password"
            name="password"
            type={showValues ? 'text' : 'password'}
            autoComplete="password"
            onChange={handleChange}
            onBlur={handleBlur}
            value={values.password}
            isInvalid={touched.password && Boolean(errors.password)}
            autoFocus
            isDisabled={loading}
            placeholder={isReset ? tx.newPsw : 'Password'}
            mb={4}
            pr={14}
          />
          <TogglePasswordVisibilityButton
            visible={showValues}
            onToggle={() => setShowValues(v => !v)}
          />
        </InputGroup>
        <FormErrorMessage ml={2}>{touched.password && errors.password}</FormErrorMessage>
      </FormControl>

      <FormControl>
        <Input
          variant="outline"
          h="48px"
          name="repeatPassword"
          type={showValues ? 'text' : 'password'}
          id="repeatPassword"
          onChange={handleChange}
          onBlur={handleBlur}
          value={values.repeatPassword}
          isInvalid={touched.repeatPassword && Boolean(errors.repeatPassword)}
          autoComplete="repeatPassword"
          isDisabled={loading}
          placeholder={isReset ? tx.repeatNewPsw : tx.repeatPsw}
        />
        <FormErrorMessage ml={2}>
          {touched.repeatPassword && errors.repeatPassword}
        </FormErrorMessage>
      </FormControl>

      <Button mt={8} type="submit" w="100%" colorScheme="primary" h="44px" isLoading={loading}>
        Submit
      </Button>
    </Form>
  );
};

export default SetPasswordForm;
